import { sumBy } from 'lodash'
import { ReactElement, useEffect, useMemo } from 'react'

import useFieldState from '../../../../../../../../1stlog-ui/src/components/Field/useFieldState'
import useFieldValue from '../../../../../../../../1stlog-ui/src/components/Field/useFieldValue'
import { getGirthLength } from '../../../../../../../../shipmetrics-shared/helpers/measurementHelpers'
type UseSperrgutLimitationProps = {
  coliFieldName: string
}

type UseSperrgutLimitation = {
  isLimitExceeded: boolean
  sperrgutAlert: ReactElement
}

export function useSperrgutLimitation(
  props: UseSperrgutLimitationProps,
): UseSperrgutLimitation {
  const { coliFieldName } = props

  const [isEnabled, setIsEnabled] = useFieldState<boolean>('isEnabled')
  const [wasAutomaticallyEnabled, setWasAutomaticallyEnabled] =
    useFieldState<boolean>('wasAutomaticallyEnabled')
  const limits: {
    weight: number
    length: number
    width: number
    height: number
    girth: number
    shipmentWeight: number
    shipmentVolumeWeight: number
  } = useFieldValue('limits')

  const weight: number | '' = useFieldValue(`${coliFieldName}.weight`, {
    ignorePrefix: true,
  })
  const length: number | '' = useFieldValue(`${coliFieldName}.length`, {
    ignorePrefix: true,
  })
  const width: number | '' = useFieldValue(`${coliFieldName}.width`, {
    ignorePrefix: true,
  })
  const height: number | '' = useFieldValue(`${coliFieldName}.height`, {
    ignorePrefix: true,
  })
  const colis: { weight: number; volumeWeight: number }[] = useFieldValue(
    'colis',
    {
      ignorePrefix: true,
    },
  )

  const coliHardLimitsExceeded: boolean = useFieldValue(
    `${coliFieldName}.coliHardLimitsExceeded`,
    {
      ignorePrefix: true,
      subscription: {
        value: true,
      },
    },
  )

  const message = useMemo(() => {
    // When hard limits are exceeded, no point in turning on the service
    if (coliHardLimitsExceeded) return null
    if (weight && weight > limits.weight) {
      return i18n.t(
        'bulk_cargo_weight_exceeds_normal_message2',
        'Durch das angegebene Gewicht ist das Coli nur als Sperrgut transportierbar.',
      )
    }
    if (length && length > limits.length) {
      return i18n.t(
        'bulk_cargo_length_exceeds_normal_message2',
        'Durch die angegebene Länge ist das Coli nur als Sperrgut transportierbar.',
      )
    }
    if (width && width > limits.width) {
      return i18n.t(
        'bulk_cargo_width_exceeds_normal_message2',
        'Durch die angegebene Breite ist das Coli nur als Sperrgut transportierbar.',
      )
    }
    if (height && height > limits.height) {
      return i18n.t(
        'bulk_cargo_height_exceeds_normal_message2',
        'Durch die angegebene Höhe ist das Coli nur als Sperrgut transportierbar.',
      )
    }
    const girth = getGirthLength({ length, width, height })
    if (girth && girth > limits.girth) {
      return i18n.t(
        'bulk_cargo_girth_exceeds_normal_message2',
        'Durch das angegebene Gurtmaß ist das Coli nur als Sperrgut transportierbar.',
      )
    }
    const shipmentWeight = sumBy(colis, 'weight')
    if (shipmentWeight && shipmentWeight > limits.shipmentWeight) {
      return i18n.t(
        'bulk_cargo_shipment_weight_exceeds_normal_message2',
        'Durch das angegebene Gewicht der Colis ist die gesamte Sendung nur als Sperrgut transportierbar.',
      )
    }

    const shipmentVolumeWeight = sumBy(colis, 'volumeWeight')
    if (
      shipmentVolumeWeight &&
      shipmentVolumeWeight > limits.shipmentVolumeWeight
    ) {
      return i18n.t(
        'bulk_cargo_shipment_volume_weight_exceeds_normal_message2',
        'Durch das angegebene Volumengewicht der Colis ist die gesamte Sendung nur als Sperrgut transportierbar.',
      )
    }
  }, [limits, weight, length, width, height, colis, coliHardLimitsExceeded])

  // Automatically enable the service if the limit is exceeded
  useEffect(() => {
    if (!isEnabled && message) {
      setIsEnabled(true)
      setWasAutomaticallyEnabled(true)
    }
  }, [isEnabled, message])

  // Automatically disable the service if the limit is not exceeded anymore
  useEffect(() => {
    if (isEnabled && !message && wasAutomaticallyEnabled) {
      setIsEnabled(false)
      setWasAutomaticallyEnabled(false)
    }
  }, [isEnabled, message])

  return {
    isLimitExceeded: !!message,
    sperrgutAlert: (
      <>
        {message}{' '}
        {i18n.t(
          'bulk_cargo_changes_to_time_info2',
          'Es können sich Änderungen bei der Zustellzeit ergeben (obenstehend).',
        )}
      </>
    ),
  }
}
